"use client";

import { useEffect, useState, useRef } from "react";
import { Fireworks } from "fireworks-js";
import "../casealert.css";

/**
 * Az itemek kepe a SITE-on el, es az adatbazisban RELATIV utvonalkent
 * (`/images/case/1/1.png`). Az overlay viszont mas origin, ott ez a
 * `overlay.palinagyonbuzi.com/images/...`-ra oldodik fel, ahol nincs ilyen fajl.
 *
 * Es nem is 404-et kapunk: a CRA fallback miatt a szerver 200-zal az index.html-t
 * adja vissza, amit az <img> nem tud dekodolni -- innen a tort kep ikon. Ezert
 * kell abszolutta tenni az utvonalat, es ezert kell onError is: a statuszkod
 * alapjan nem lehet eldonteni, hogy megjott-e a kep.
 */
const SITE_URL = (
  process.env.REACT_APP_SITE_URL || "https://neverdoit.com"
).replace(/\/+$/, "");

/** Ha az item nem hoz sajat szint, ez a studio alap neonja. */
const DEFAULT_COLOR = "#00ffff";
/** Meddig all a kepernyon. A .rare-timer animacioja ugyanezt hasznalja. */
const HOLD_MS = 8000;

function resolveItemImage(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return null;
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  return `${SITE_URL}/${value.replace(/^\/+/, "")}`;
}

/**
 * #rgb / #rrggbb -> "r, g, b". A CSS-ben `rgba(var(--rare-rgb), .35)` lesz belole.
 *
 * Szandekosan NEM `color-mix()`: az Chromium 111 felett mukodik, es az OBS
 * bongeszo-forrasa reg lemaradhat a gazda gepen. Egy CSS valtozoba tett RGB
 * harmas mindenhol megy.
 */
function toRgbTriplet(hex, fallback = DEFAULT_COLOR) {
  const raw = String(hex ?? "").trim();
  const source = /^#?[0-9a-f]{3}$|^#?[0-9a-f]{6}$/i.test(raw) ? raw : fallback;
  let body = source.replace("#", "");
  if (body.length === 3)
    body = body
      .split("")
      .map((c) => c + c)
      .join("");
  const value = Number.parseInt(body, 16);
  return `${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}`;
}

export default function RareOpenAlert({ data }) {
  const [visible, setVisible] = useState(true);
  const [imageFailed, setImageFailed] = useState(false);
  const containerRef = useRef(null);
  const fireworksRef = useRef(null);

  const imageUrl = resolveItemImage(data.image);
  const percent = Number(data.odds ?? 0);
  const itemName = String(data.itemName ?? "").trim();
  const openerName = String(data.name ?? "").trim();
  // Az avataron a nyito kezdobetuje all. Ures nev eseten a placeholder jobb,
  // mint egy ures korlap.
  const openerInitial = openerName.charAt(0).toUpperCase() || "?";

  useEffect(() => {
    if (containerRef.current && visible) {
      const fireworks = new Fireworks(containerRef.current, {
        autoresize: true,
        opacity: 1,
        acceleration: 1.0,
        friction: 0.97,
        gravity: 1.5,
        particles: 50,
        traceLength: 3,
        traceSpeed: 10,
        explosion: 5,
        intensity: 30,
        flickering: 50,
        lineStyle: "round",
        hue: {
          min: 0,
          max: 360,
        },
        delay: {
          min: 30,
          max: 60,
        },
        rocketsPoint: {
          min: 50,
          max: 50,
        },
        lineWidth: {
          explosion: 5,
          trace: {
            min: 1,
            max: 2,
          },
        },
        brightness: {
          min: 50,
          max: 80,
        },
        decay: {
          min: 0.015,
          max: 0.03,
        },
        mouse: {
          click: false,
          move: false,
          max: 1,
        },
      });

      fireworksRef.current = fireworks;
      fireworks.start();

      const timer = setTimeout(() => {
        fireworks.stop();
        setVisible(false);
      }, HOLD_MS);

      return () => {
        clearTimeout(timer);
        fireworks.stop();
      };
    }
  }, [visible]);

  return (
    <div className={`rare-open-wrapper ${visible ? "fade-in" : "fade-out"}`}>
      <div ref={containerRef} className="fireworks-canvas"></div>

      {/* Az item sajat szine egy valtozoban: innentol a keret, a ragyogas, az
          avatar es a szazalek badge is ebbol dolgozik. */}
      <div
        className="rare-panel"
        style={{ "--rare-rgb": toRgbTriplet(data.color) }}
      >
        <div className="rare-head">
          <span className="rare-pill">RARE DROP</span>
          {data.caseName ? (
            <span className="rare-case">{data.caseName}</span>
          ) : null}
        </div>

        <div className="rare-art">
          {imageUrl && !imageFailed ? (
            <img
              src={imageUrl}
              alt={itemName || "item"}
              className="rare-art-img"
              onError={() => setImageFailed(true)}
            />
          ) : (
            // Inkabb az item nevenek kezdobetuje, mint a bongeszo tort-kep
            // ikonja: adasban az utobbi ugy nez ki, mintha elszallt volna az
            // overlay.
            <div className="rare-art-fallback">
              {itemName.charAt(0).toUpperCase() || "?"}
            </div>
          )}

          <div className="rare-badge">
            <span className="rare-badge-value">{percent.toFixed(2)}%</span>
            <span className="rare-badge-label">CHANCE</span>
          </div>
        </div>

        <div className="rare-item-name">{itemName}</div>

        <div className="rare-foot">
          <span className="rare-avatar">{openerInitial}</span>
          opened by <b>{openerName}</b>
        </div>

        {/* A visszaszamlalo CSS animacio, nem React allapot: az alert ugyis
            fix ideig all, egy masodpercenkenti render felesleges lenne. */}
        <div className="rare-timer" />
      </div>
    </div>
  );
}
